import React, { useEffect, useState } from 'react';
import { Loader2, ShieldAlert, Award } from 'lucide-react';
import { aaiApi } from '../../services/aaiApi';
import {
  AAI_DIMENSIONS,
  DIMENSION_LABELS,
  type Aggregate,
  type DimensionRange,
  type AaiStage,
} from '../../types/aai';

// ── stage presentation ──────────────────────────────────────────────────────

const STAGE_META: Record<string, { label: string; className: string }> = {
  nascent: { label: 'Nascent', className: 'bg-gray-100 text-gray-700 ring-gray-200' },
  early: { label: 'Early', className: 'bg-amber-50 text-amber-700 ring-amber-200' },
  developing: { label: 'Developing', className: 'bg-brand-50 text-brand-700 ring-brand-200' },
  mature: { label: 'Mature', className: 'bg-indigo-50 text-indigo-700 ring-indigo-200' },
  advanced: { label: 'Advanced', className: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
};

export const StagePill: React.FC<{ stage?: AaiStage | string | null }> = ({ stage }) => {
  const meta = (stage && STAGE_META[stage]) || { label: stage || '—', className: 'bg-gray-100 text-gray-600 ring-gray-200' };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${meta.className}`}>
      {meta.label}
    </span>
  );
};

// ── dimension ranges (R2: always a band, never a single number) ─────────────

const pct = (n: number) => `${Math.round(Math.max(0, Math.min(1, n)) * 100)}%`;

const RangeBar: React.FC<{ label: string; range?: DimensionRange }> = ({ label, range }) => {
  const hasRange = range && typeof range.low === 'number' && typeof range.high === 'number';
  const low = hasRange ? range!.low : 0;
  const high = hasRange ? range!.high : 0;
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm text-gray-700">{label}</span>
        <span className="text-xs tabular-nums text-gray-500">
          {hasRange ? `${pct(low)}–${pct(high)}` : 'no signal yet'}
        </span>
      </div>
      <div className="relative h-2 w-full rounded-full bg-gray-100">
        {hasRange && (
          <div
            className="absolute h-2 rounded-full bg-brand-500"
            style={{ left: pct(low), width: pct(high - low) }}
          />
        )}
      </div>
    </div>
  );
};

export const DimensionRanges: React.FC<{ dimensions?: Record<string, DimensionRange> | null }> = ({ dimensions }) => (
  <div className="space-y-3">
    {AAI_DIMENSIONS.map((dim) => (
      <RangeBar key={dim} label={DIMENSION_LABELS[dim]} range={dimensions?.[dim]} />
    ))}
  </div>
);

// ── aggregate (department / company rollup, with N-suppression) ──────────────

export const AggregateView: React.FC<{ title: string; agg?: Aggregate | null }> = ({ title, agg }) => {
  if (!agg) return null;
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        <span className="text-xs text-gray-500">{agg.onboarded_count} onboarded</span>
      </div>

      {agg.suppressed ? (
        <div className="flex items-start gap-2 rounded-lg bg-gray-50 border border-gray-100 p-3">
          <ShieldAlert size={16} className="text-gray-400 mt-0.5 shrink-0" />
          <p className="text-sm text-gray-600">
            {agg.suppressed_reason || 'Hidden to protect privacy until the group is large enough.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <DimensionRanges dimensions={agg.dimensions} />
          <StageDistribution distribution={agg.stage_distribution} total={agg.onboarded_count} />
        </div>
      )}
    </div>
  );
};

const STAGE_BAR: Record<string, string> = {
  nascent: 'bg-gray-300',
  early: 'bg-amber-400',
  developing: 'bg-brand-500',
  mature: 'bg-indigo-500',
  advanced: 'bg-emerald-500',
};

const StageDistribution: React.FC<{ distribution?: Record<string, number> | null; total: number }> = ({ distribution, total }) => {
  if (!distribution) return null;
  return (
    <div>
      <p className="text-sm font-medium text-gray-700 mb-2">Stage mix</p>
      <div className="space-y-2">
        {Object.entries(distribution).map(([stage, count]) => (
          <div key={stage} className="flex items-center gap-3">
            <span className="w-24 text-xs text-gray-600 capitalize">{stage}</span>
            <div className="flex-1 h-2 rounded-full bg-gray-100">
              <div
                className={`h-2 rounded-full ${STAGE_BAR[stage] || 'bg-gray-300'}`}
                style={{ width: total > 0 ? `${Math.round((count / total) * 100)}%` : '0%' }}
              />
            </div>
            <span className="w-6 text-right text-xs tabular-nums text-gray-500">{count}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── tiny shared states ───────────────────────────────────────────────────────

export const Loading: React.FC<{ label?: string }> = ({ label = 'Loading…' }) => (
  <div className="flex items-center gap-2 text-sm text-gray-500 py-10 justify-center">
    <Loader2 size={16} className="animate-spin" /> {label}
  </div>
);

export const ErrorNote: React.FC<{ message: string }> = ({ message }) => (
  <div className="rounded-lg bg-red-50 border border-red-100 p-3 text-sm text-red-700">{message}</div>
);

// AA certification stamp — shows the highest credential the caller holds
// ('AA Certified' at 6-month, else 'AA Certified Operator' at 3-month).
export const CertBadge: React.FC<{ className?: string }> = ({ className = '' }) => {
  const [label, setLabel] = useState<string | null>(null);
  useEffect(() => {
    aaiApi.getMyCertifications()
      .then((r) => {
        const keys = new Set((r.data.certifications || []).map((c) => c.cert_key));
        setLabel(keys.has('aa_certified') ? 'AA Certified' : keys.has('aa_operator') ? 'AA Certified Operator' : null);
      })
      .catch(() => undefined);
  }, []);
  if (!label) return null;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200 px-2.5 py-0.5 text-[11px] font-semibold ${className}`}>
      <Award size={12} /> {label}
    </span>
  );
};
